import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloseIcon } from "../../Icons";
import { BRAND_THEME } from "../../ui/brandTheme";
import PromoHeroArt from "./PromoHeroArt";

const PromoCampaignModal = ({ campaign, onClose }) => {
  const navigate = useNavigate();
  const titleId = useId();
  const slides = Array.isArray(campaign?.slides) ? campaign.slides : [];
  const last = Math.max(slides.length - 1, 0);
  const [index, setIndex] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const dontShowRef = useRef(false);
  const slide = slides[index] || slides[0];

  dontShowRef.current = dontShowAgain;

  const close = useCallback(() => {
    onClose?.(dontShowRef.current);
  }, [onClose]);

  const goPrev = useCallback(() => {
    setIndex((i) => Math.max(i - 1, 0));
  }, []);

  const goNext = useCallback(() => {
    setIndex((i) => Math.min(i + 1, last));
  }, [last]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "ArrowLeft") goPrev();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [close, goNext, goPrev]);

  if (!slide) return null;

  const goCta = () => {
    const to = campaign.ctaTo;
    onClose?.(dontShowAgain);
    if (to) navigate(to);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50"
        aria-label="Close announcement"
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative w-full max-w-[34rem] overflow-hidden rounded-2xl border bg-white shadow-2xl ${BRAND_THEME.section.wrap}`}
      >
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/95 text-slate-700 shadow-sm hover:bg-white"
          aria-label="Close"
        >
          <CloseIcon size={15} />
        </button>

        <PromoHeroArt
          src={slide.image}
          alt={slide.imageAlt || ""}
          onPrev={goPrev}
          onNext={goNext}
          canPrev={index > 0}
          canNext={index < last}
        />

        <div className="px-5 pb-4 pt-4 sm:px-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-indigo-500">
            {slide.kicker}
          </p>
          <h2
            id={titleId}
            className="mt-1 text-[1.45rem] font-bold leading-snug tracking-tight text-slate-900 sm:text-[1.6rem]"
          >
            {slide.title}
          </h2>
          <p className="mt-2.5 text-[13.5px] leading-relaxed text-slate-600">
            {slide.body}
          </p>

          {slide.highlight ? (
            <p className="mt-3 inline-flex rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-[11px] font-semibold text-indigo-700">
              {slide.highlight}
            </p>
          ) : null}

          {campaign.ctaLabel && campaign.ctaTo ? (
            <button
              type="button"
              onClick={goCta}
              className={`mt-4 flex h-10 w-full items-center justify-center rounded-xl text-sm font-semibold ${BRAND_THEME.button.primary}`}
            >
              {campaign.ctaLabel}
            </button>
          ) : null}

          <div className="mt-4 flex items-center justify-between gap-3">
            {slides.length > 1 ? (
              <div
                className="flex items-center gap-1.5"
                role="tablist"
                aria-label="Campaign slides"
              >
                {slides.map((item, i) => {
                  const active = i === index;
                  return (
                    <button
                      key={item.id || i}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      aria-label={`Slide ${i + 1}`}
                      onClick={() => setIndex(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        active
                          ? "w-5 bg-indigo-600"
                          : "w-1.5 bg-slate-300 hover:bg-slate-400"
                      }`}
                    />
                  );
                })}
              </div>
            ) : (
              <span />
            )}
            <label className="inline-flex cursor-pointer items-center gap-2 text-[11px] text-slate-500">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-200"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              Don&apos;t show this again
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCampaignModal;
